/**
 * Champs types des entrees « carte bancaire », « identite » (R5), « reseau Wi-Fi », « compte bancaire »,
 * « portefeuille crypto », « base de donnees », « cle d'API », « licence logicielle », « serveur », « compte de
 * messagerie », « carte de membre » et « dossier medical ».
 *
 * Les valeurs vivent UNIQUEMENT dans les secrets chiffres de l'entree
 * (`secrets.card` / `secrets.identity` / `secrets.wifi` / `secrets.bank` / `secrets.wallet` / `secrets.database` /
 * `secrets['api-key']` / `secrets['software-license']` / `secrets.server` / `secrets.email` / `secrets.membership` / `secrets.medical`, objets `cle -> chaine`), jamais en
 * metadonnee claire. Ce module est pur : validation (Luhn, expiration, IBAN, BIC),
 * normalisation et masquage pour l'affichage ; aucune E/S.
 */
import type { EntrySecrets } from '@klavox/crypto-core';
import type { VaultItemType } from '@klavox/vault-model';
import { normalizeRecoveryPhrase } from './recovery-phrase.js';
import { parseSshPublicKey, readSshKey } from './ssh-key-item.js';

export type TypedFieldGroup = 'card' | 'identity' | 'wifi' | 'bank' | 'wallet' | 'database' | 'api-key' | 'software-license' | 'server' | 'email' | 'membership' | 'medical';
export type TypedFields = Readonly<Record<string, string>>;

export interface TypedFieldSpec {
  readonly key: string;
  readonly label: string;
  /** Masque par defaut (fiche et editeur), revele a la demande. */
  readonly sensitive?: boolean;
  readonly inputMode?: 'numeric' | 'tel' | 'email' | 'text';
  readonly placeholder?: string;
  readonly wide?: boolean;
  /**
   * Zone de texte plutot que champ d'une ligne : un `<input>` SUPPRIME les sauts de ligne a la saisie (assainissement
   * HTML), une phrase collee « un mot par ligne » y perdrait ses separateurs.
   */
  readonly multiline?: boolean;
  /** Liste fermee (`<select>`) : seules ces valeurs sont gardees ; la premiere option vide = « non renseigne ». */
  readonly options?: readonly { readonly value: string; readonly label: string }[];
}

export const CARD_FIELDS: readonly TypedFieldSpec[] = [
  { key: 'cardholder', label: 'Titulaire', wide: true },
  { key: 'number', label: 'Numero de carte', sensitive: true, inputMode: 'numeric', placeholder: '0000 0000 0000 0000', wide: true },
  { key: 'expiry', label: 'Expiration', inputMode: 'numeric', placeholder: 'MM/AA' },
  { key: 'cvv', label: 'Cryptogramme (CVV)', sensitive: true, inputMode: 'numeric', placeholder: '123' },
  { key: 'pin', label: 'Code PIN', sensitive: true, inputMode: 'numeric' },
  { key: 'bank', label: 'Banque emettrice' },
];

export const IDENTITY_FIELDS: readonly TypedFieldSpec[] = [
  { key: 'title', label: 'Civilite' },
  { key: 'firstName', label: 'Prenom' },
  { key: 'lastName', label: 'Nom' },
  { key: 'birthDate', label: 'Date de naissance', placeholder: 'AAAA-MM-JJ' },
  { key: 'email', label: 'E-mail', inputMode: 'email' },
  { key: 'phone', label: 'Telephone', inputMode: 'tel' },
  { key: 'company', label: 'Societe' },
  { key: 'address', label: 'Adresse', wide: true },
  { key: 'postalCode', label: 'Code postal' },
  { key: 'city', label: 'Ville' },
  { key: 'region', label: 'Region / Etat' },
  { key: 'country', label: 'Pays' },
  { key: 'idCardNumber', label: 'N° carte d’identite', sensitive: true },
  { key: 'idCardExpiry', label: 'Carte d’identite valable jusqu’au', placeholder: 'AAAA-MM-JJ' },
  { key: 'passportNumber', label: 'N° passeport', sensitive: true },
  { key: 'passportExpiry', label: 'Passeport valable jusqu’au', placeholder: 'AAAA-MM-JJ' },
  { key: 'licenseNumber', label: 'N° permis de conduire', sensitive: true },
  { key: 'licenseExpiry', label: 'Permis valable jusqu’au', placeholder: 'AAAA-MM-JJ' },
  { key: 'socialSecurityNumber', label: 'N° securite sociale', sensitive: true },
  { key: 'taxNumber', label: 'N° fiscal', sensitive: true },
];

/**
 * Securite d'un reseau Wi-Fi : les valeurs de `networkSecurityType` du FIDO CXF v1.0 (credential `wifi`), gardees
 * telles quelles pour que l'export et l'import n'aient rien a traduire.
 */
export const WIFI_SECURITY_TYPES = ['wpa3-personal', 'wpa2-personal', 'wpa-personal', 'wep', 'unsecured'] as const;
export type WifiSecurity = (typeof WIFI_SECURITY_TYPES)[number];

const WIFI_SECURITY_LABELS: Record<WifiSecurity, string> = {
  'wpa3-personal': 'WPA3 Personnel',
  'wpa2-personal': 'WPA2 Personnel',
  'wpa-personal': 'WPA Personnel',
  wep: 'WEP',
  unsecured: 'Aucune (reseau ouvert)',
};

/**
 * Le mot de passe du reseau n'est PAS un champ du groupe : il vit dans `secrets.password`, comme celui d'un
 * identifiant -- historique, Sante du coffre (faible, reutilise, fuite) et generateur s'y appliquent sans exception.
 */
export const WIFI_FIELDS: readonly TypedFieldSpec[] = [
  { key: 'ssid', label: 'Nom du reseau (SSID)', wide: true },
  {
    key: 'security',
    label: 'Securite',
    options: [{ value: '', label: 'Non renseignee' }, ...WIFI_SECURITY_TYPES.map((value) => ({ value, label: WIFI_SECURITY_LABELS[value] }))],
  },
  { key: 'hidden', label: 'Reseau masque', options: [{ value: '', label: 'Non' }, { value: 'true', label: 'Oui' }] },
];

/** Ecritures de la securite chez les autres gestionnaires (1Password `wpa2p`, Proton Pass `WPA2` ou 2…) -> valeur CXF. */
const WIFI_SECURITY_ALIASES: Record<string, WifiSecurity> = {
  wpa3: 'wpa3-personal',
  wpa3p: 'wpa3-personal',
  'wpa3 personal': 'wpa3-personal',
  'wpa3 personnel': 'wpa3-personal',
  wpa2: 'wpa2-personal',
  wpa2p: 'wpa2-personal',
  'wpa2 personal': 'wpa2-personal',
  'wpa2 personnel': 'wpa2-personal',
  wpa: 'wpa-personal',
  'wpa personal': 'wpa-personal',
  'wpa personnel': 'wpa-personal',
  wep: 'wep',
  none: 'unsecured',
  open: 'unsecured',
  nopass: 'unsecured',
};

/** Securite lue telle qu'un export l'ecrit ; `null` si elle ne designe aucune valeur connue (entreprise, inconnue). */
export function wifiSecurity(raw: string): WifiSecurity | null {
  const text = raw.trim().toLowerCase().replace(/[_-]+/gu, ' ').replace(/\s+/gu, ' ');
  const canonical = WIFI_SECURITY_TYPES.find((value) => value.replace('-', ' ') === text);
  return canonical ?? WIFI_SECURITY_ALIASES[text] ?? null;
}

export function wifiSecurityLabel(security: WifiSecurity): string {
  return WIFI_SECURITY_LABELS[security];
}

/**
 * Le mot de passe peut-il servir sur ce reseau ? Une cle WPA compte 8 a 63 caracteres ASCII imprimables ou 64 chiffres
 * hexadecimaux (IEEE 802.11i, annexe H.4) ; une cle WEP 5 ou 13 caracteres ASCII, ou 10 ou 26 chiffres hexadecimaux.
 * Retourne la phrase a afficher, `null` si la cle convient ou si rien ne permet d'en juger (securite non renseignee,
 * reseau ouvert, mot de passe vide).
 */
export function wifiPasswordProblem(security: string, password: string): string | null {
  if (password.length === 0) return null;
  const ascii = /^[\x20-\x7e]*$/u.test(password);
  const hex = /^[0-9a-f]*$/iu.test(password);
  if (security === 'wpa3-personal' || security === 'wpa2-personal' || security === 'wpa-personal') {
    if ((ascii && password.length >= 8 && password.length <= 63) || (hex && password.length === 64)) return null;
    return 'Une cle WPA compte 8 a 63 caracteres ASCII imprimables (ou 64 chiffres hexadecimaux) : un appareil refuserait celle-ci.';
  }
  if (security === 'wep') {
    if ((ascii && (password.length === 5 || password.length === 13)) || (hex && (password.length === 10 || password.length === 26))) return null;
    return 'Une cle WEP compte 5 ou 13 caracteres ASCII (ou 10 ou 26 chiffres hexadecimaux) : un appareil refuserait celle-ci.';
  }
  return null;
}

/** Nature du compte : les valeurs du menu « type » d'un compte bancaire 1Password, gardees telles quelles. */
export const BANK_ACCOUNT_TYPES = ['checking', 'savings', 'loc', 'atm', 'money market', 'other'] as const;
export type BankAccountType = (typeof BANK_ACCOUNT_TYPES)[number];

const BANK_ACCOUNT_TYPE_LABELS: Record<BankAccountType, string> = {
  checking: 'Compte courant',
  savings: 'Compte d’epargne',
  loc: 'Ligne de credit',
  atm: 'Carte de retrait',
  'money market': 'Compte de placement',
  other: 'Autre',
};

/**
 * Compte bancaire. Les cles sont distinctes de celles de la carte (`bank`, `pin`, `number`) : le brouillon de l'editeur
 * range tous les groupes dans un seul objet, changer de type ne doit rien melanger. Le code d'acces (banque par
 * telephone, PIN du compte) est masque ; l'IBAN et le numero de compte aussi, comme le numero d'une carte.
 */
export const BANK_FIELDS: readonly TypedFieldSpec[] = [
  { key: 'bankName', label: 'Banque' },
  { key: 'accountHolder', label: 'Titulaire du compte' },
  {
    key: 'accountType',
    label: 'Nature du compte',
    options: [{ value: '', label: 'Non renseignee' }, ...BANK_ACCOUNT_TYPES.map((value) => ({ value, label: BANK_ACCOUNT_TYPE_LABELS[value] }))],
  },
  { key: 'iban', label: 'IBAN', sensitive: true, placeholder: 'FR76 3000 6000 0112 3456 7890 189', wide: true },
  { key: 'bic', label: 'BIC (SWIFT)', placeholder: 'AGRIFRPP' },
  { key: 'accountNumber', label: 'Numero de compte (hors IBAN)', sensitive: true },
  { key: 'routingNumber', label: 'Code banque / guichet (routing, sort code)' },
  { key: 'bankPin', label: 'Code d’acces / PIN', sensitive: true, inputMode: 'numeric' },
];

/** Nature du compte lue telle qu'un export l'ecrit ; `null` si elle ne designe aucune valeur connue. */
export function bankAccountType(raw: string): BankAccountType | null {
  const text = raw.trim().toLowerCase().replace(/[_-]+/gu, ' ').replace(/\s+/gu, ' ');
  return BANK_ACCOUNT_TYPES.find((value) => value === text) ?? null;
}

export function bankAccountTypeLabel(type: BankAccountType): string {
  return BANK_ACCOUNT_TYPE_LABELS[type];
}

/**
 * Longueur de l'IBAN par pays, d'apres le registre IBAN de SWIFT (autorite d'enregistrement ISO 13616). Un territoire
 * rattache (Guadeloupe, Jersey, Aland…) emploie le code de son pays de rattachement (FR, GB, FI) : il n'a pas de ligne.
 */
const IBAN_LENGTHS: Readonly<Record<string, number>> = {
  AD: 24, AE: 23, AL: 28, AT: 20, AZ: 28, BA: 20, BE: 16, BG: 22, BH: 22, BI: 27, BR: 29, BY: 28, CH: 21, CR: 22,
  CY: 28, CZ: 24, DE: 22, DJ: 27, DK: 18, DO: 28, EE: 20, EG: 29, ES: 24, FI: 18, FK: 18, FO: 18, FR: 27, GB: 22,
  GE: 22, GI: 23, GL: 18, GR: 27, GT: 28, HN: 28, HR: 21, HU: 28, IE: 22, IL: 23, IQ: 23, IS: 26, IT: 27, JO: 30,
  KW: 30, KZ: 20, LB: 28, LC: 32, LI: 21, LT: 20, LU: 20, LV: 21, LY: 25, MC: 27, MD: 24, ME: 22, MK: 19, MN: 20,
  MR: 27, MT: 31, MU: 30, NI: 28, NL: 18, NO: 15, OM: 23, PK: 24, PL: 28, PS: 29, PT: 25, QA: 29, RO: 24, RS: 22,
  RU: 33, SA: 24, SC: 31, SD: 18, SE: 24, SI: 19, SK: 24, SM: 27, SO: 23, ST: 25, SV: 28, TL: 23, TN: 24, TR: 26,
  UA: 29, VA: 22, VG: 24, XK: 20, YE: 30,
};

/** IBAN au format electronique (ISO 13616) : majuscules, sans espace ni tiret. */
export function normalizeIban(value: string): string {
  return value.replace(/[\s-]+/gu, '').toUpperCase();
}

/**
 * L'IBAN est-il recevable ? Forme `PP CC BBAN` (pays, deux chiffres de controle, 11 a 30 caracteres alphanumeriques),
 * longueur du registre pour un pays connu, et cle ISO 7064 MOD 97-10 : les quatre premiers caracteres passent a la fin,
 * chaque lettre devient deux chiffres (A = 10 … Z = 35), le reste de la division par 97 doit valoir 1. Un pays absent du
 * registre n'est pas refuse pour sa seule longueur (le registre s'etend), mais sa cle doit tenir.
 */
export function isIbanValid(value: string): boolean {
  const iban = normalizeIban(value);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/u.test(iban)) return false;
  const length = IBAN_LENGTHS[iban.slice(0, 2)];
  if (length !== undefined && iban.length !== length) return false;
  let remainder = 0;
  for (const char of iban.slice(4) + iban.slice(0, 4)) {
    const digits = /\d/u.test(char) ? char : String(char.charCodeAt(0) - 55);
    for (const digit of digits) remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder === 1;
}

/** Format papier de l'IBAN : groupes de quatre caracteres (ISO 13616-1, § 5.4). */
export function formatIban(value: string): string {
  return normalizeIban(value).replace(/(.{4})(?=.)/gu, '$1 ');
}

/** `FR76 •••• 0189` : pays, cle et quatre derniers caracteres, jamais davantage. */
export function maskIban(value: string): string {
  const iban = normalizeIban(value);
  return iban.length <= 8 ? '••••' : `${iban.slice(0, 4)} •••• ${iban.slice(-4)}`;
}

/** BIC (ISO 9362) : 4 caracteres d'etablissement, 2 lettres de pays, 2 de localisation, 3 optionnels de succursale. */
export function isBicValid(value: string): boolean {
  return /^[A-Z0-9]{4}[A-Z]{2}[A-Z0-9]{2}(?:[A-Z0-9]{3})?$/u.test(value.replace(/\s+/gu, '').toUpperCase());
}

/**
 * Ce qui empeche d'enregistrer le compte : un IBAN ou un BIC qu'aucun virement n'accepterait. Retourne la phrase a
 * afficher, `null` si rien n'est renseigne ou si tout est recevable.
 */
export function bankAccountProblem(fields: Readonly<Record<string, string>>): string | null {
  const iban = (fields['iban'] ?? '').trim();
  if (iban.length > 0 && !isIbanValid(iban)) return 'IBAN invalide : la cle de controle ou la longueur ne correspond pas (verifiez chaque caractere).';
  const bic = (fields['bic'] ?? '').trim();
  if (bic.length > 0 && !isBicValid(bic)) return 'BIC invalide : 8 ou 11 caracteres attendus (ex. AGRIFRPP ou AGRIFRPP882).';
  return null;
}

/**
 * Portefeuille crypto. La phrase de recuperation (et la phrase de passe BIP39 qui la complete, le « 25e mot ») donnent
 * acces aux fonds sans rien d'autre : masquees, jamais cherchees. Le mot de passe qui deverrouille l'application ou le
 * fichier du portefeuille vit dans `secrets.password` (historique et Sante du coffre s'y appliquent). L'adresse de
 * reception est publique : elle se cherche et se copie.
 */
export const WALLET_FIELDS: readonly TypedFieldSpec[] = [
  { key: 'walletApp', label: 'Portefeuille (application ou materiel)', placeholder: 'Ledger, Trezor, Electrum, MetaMask…' },
  { key: 'blockchain', label: 'Reseau', placeholder: 'Bitcoin, Ethereum…' },
  { key: 'recoveryPhrase', label: 'Phrase de recuperation', sensitive: true, wide: true, multiline: true, placeholder: '12 a 24 mots, separes par des espaces ou des retours a la ligne' },
  { key: 'seedPassphrase', label: 'Phrase de passe BIP39 (« 25e mot »)', sensitive: true },
  { key: 'walletAddress', label: 'Adresse de reception', wide: true },
];

/** `bc1qar0s…5mdq` : debut et fin d'une adresse, de quoi la reconnaitre dans la liste. */
export function shortWalletAddress(address: string): string {
  return address.length <= 16 ? address : `${address.slice(0, 8)}…${address.slice(-4)}`;
}

/**
 * Moteur d'une base de donnees : les valeurs du menu « type » d'une base 1Password (gardees telles quelles pour que
 * l'import n'ait rien a traduire), plus MariaDB, MongoDB et Redis, que 1Password range en « autre ».
 */
export const DATABASE_ENGINES = ['postgresql', 'mysql', 'mariadb', 'mssql', 'oracle', 'db2', 'sqlite', 'mongodb', 'redis', 'filemaker', 'msaccess', 'other'] as const;
export type DatabaseEngine = (typeof DATABASE_ENGINES)[number];

const DATABASE_ENGINE_LABELS: Record<DatabaseEngine, string> = {
  postgresql: 'PostgreSQL',
  mysql: 'MySQL',
  mariadb: 'MariaDB',
  mssql: 'Microsoft SQL Server',
  oracle: 'Oracle',
  db2: 'IBM Db2',
  sqlite: 'SQLite',
  mongodb: 'MongoDB',
  redis: 'Redis',
  filemaker: 'FileMaker',
  msaccess: 'Microsoft Access',
  other: 'Autre',
};

/**
 * Base de donnees. L'identifiant et le mot de passe de connexion vivent dans `secrets.username` et `secrets.password`,
 * comme ceux d'un identifiant : historique, Sante du coffre (faible, reutilise, fuite) et generateur s'y appliquent.
 * Les cles sont propres au groupe (`host`, pas `bank` ni `ssid`) : le brouillon de l'editeur range tous les groupes dans
 * un seul objet, changer de type ne doit rien melanger.
 */
export const DATABASE_FIELDS: readonly TypedFieldSpec[] = [
  {
    key: 'engine',
    label: 'Moteur',
    options: [{ value: '', label: 'Non renseigne' }, ...DATABASE_ENGINES.map((value) => ({ value, label: DATABASE_ENGINE_LABELS[value] }))],
  },
  { key: 'host', label: 'Serveur (hote)', placeholder: 'db.example.eu', wide: true },
  { key: 'port', label: 'Port', inputMode: 'numeric', placeholder: '5432' },
  { key: 'databaseName', label: 'Base' },
  { key: 'sid', label: 'SID / service (Oracle)' },
  { key: 'connectionAlias', label: 'Alias (tnsnames, DSN)' },
  { key: 'connectionOptions', label: 'Options de connexion', placeholder: 'sslmode=verify-full', wide: true },
];

/** Moteur lu tel qu'un export l'ecrit (`PostgreSQL`, `postgres`, `sql server`…) ; `null` s'il ne designe aucune valeur connue. */
export function databaseEngine(raw: string): DatabaseEngine | null {
  const text = raw.trim().toLowerCase().replace(/[_-]+/gu, ' ').replace(/\s+/gu, ' ');
  const aliases: Record<string, DatabaseEngine> = { postgres: 'postgresql', 'sql server': 'mssql', 'microsoft sql server': 'mssql', sqlserver: 'mssql', mongo: 'mongodb' };
  return DATABASE_ENGINES.find((value) => value === text) ?? aliases[text] ?? null;
}

export function databaseEngineLabel(engine: DatabaseEngine): string {
  return DATABASE_ENGINE_LABELS[engine];
}

/** Port d'ecoute par defaut, pour l'indication de l'editeur ; `null` pour un moteur sans port reseau ou sans usage fixe. */
const DATABASE_DEFAULT_PORTS: Partial<Record<DatabaseEngine, number>> = {
  postgresql: 5432,
  mysql: 3306,
  mariadb: 3306,
  mssql: 1433,
  oracle: 1521,
  db2: 50000,
  mongodb: 27017,
  redis: 6379,
};

export function databaseDefaultPort(engine: string): number | null {
  const known = databaseEngine(engine);
  return known === null ? null : (DATABASE_DEFAULT_PORTS[known] ?? null);
}

/** Port TCP utilisable : un entier de 1 a 65535, ecrit en chiffres (base de donnees, messagerie). */
function isPortNumber(port: string): boolean {
  return /^\d{1,5}$/u.test(port) && Number(port) >= 1 && Number(port) <= 65535;
}

/**
 * Ce qui empeche d'enregistrer la base : un port hors de 1-65535 (aucun client ne s'y connecterait) ou une adresse de
 * connexion entiere collee dans « Serveur » (le mot de passe qu'elle porte finirait en clair dans un champ cherchable,
 * hors de l'historique et de la Sante du coffre). Retourne la phrase a afficher, `null` si tout est recevable.
 */
export function databaseProblem(fields: Readonly<Record<string, string>>): string | null {
  const port = (fields['port'] ?? '').trim();
  if (port.length > 0 && !isPortNumber(port)) {
    return 'Port invalide : un nombre entier de 1 a 65535 est attendu (ex. 5432).';
  }
  const host = (fields['host'] ?? '').trim();
  // `://` ou qu'il soit : `postgresql://…`, mais aussi `jdbc:mysql://…` et `Server=tcp://…`.
  if (host.includes('://')) {
    return 'Le champ « Serveur » attend l’hote seul (ex. db.example.eu) : l’identifiant et le mot de passe ont leurs propres champs.';
  }
  return null;
}

/** Schemas d'URI de connexion publies par chaque moteur (libpq, Connector/J, MongoDB, Redis). */
const DATABASE_URI_SCHEMES: Partial<Record<DatabaseEngine, string>> = {
  postgresql: 'postgresql',
  mysql: 'mysql',
  mariadb: 'mariadb',
  mongodb: 'mongodb',
  redis: 'redis',
};

/**
 * Adresse de connexion SANS mot de passe (`postgresql://ana@db.example.eu:5432/compta?sslmode=verify-full`) : de quoi
 * la coller dans `psql`, `mysql` ou un client graphique, qui demandera le mot de passe. Identifiant et base sont encodes
 * (RFC 3986), un hote IPv6 est mis entre crochets ; les options ne suivent que si elles sont deja en `cle=valeur`.
 * `null` pour un moteur sans forme d'URI standard ou sans hote.
 */
export function databaseConnectionUri(fields: Readonly<Record<string, string>>, username: string): string | null {
  const engine = databaseEngine(fields['engine'] ?? '');
  const scheme = engine === null ? undefined : DATABASE_URI_SCHEMES[engine];
  const host = (fields['host'] ?? '').trim();
  if (scheme === undefined || host.length === 0 || /[\s/?#@]/u.test(host)) return null;
  const user = username.trim();
  const port = (fields['port'] ?? '').trim();
  const name = (fields['databaseName'] ?? '').trim();
  const options = (fields['connectionOptions'] ?? '').trim();
  const hostPart = host.includes(':') && !host.startsWith('[') ? `[${host}]` : host;
  return (
    `${scheme}://${user.length > 0 ? `${encodeURIComponent(user)}@` : ''}${hostPart}` +
    (/^\d{1,5}$/u.test(port) ? `:${port}` : '') +
    (name.length > 0 ? `/${encodeURIComponent(name)}` : '') +
    (/^[^\s=&?#]+=[^\s&?#]*(?:&[^\s=&?#]+=[^\s&?#]*)*$/u.test(options) ? `${name.length > 0 ? '' : '/'}?${options}` : '')
  );
}

/**
 * Cle d'API (« API Credential » de 1Password, credential `api-key` du FIDO CXF v1.0). La cle elle-meme vit dans
 * `secrets.password` et l'identifiant (ID de cle, compte de service) dans `secrets.username`, comme ceux d'un identifiant :
 * historique (les anciennes cles d'une rotation), Sante du coffre (reutilisee, fuite) et copie s'y appliquent sans
 * exception. Cles propres au groupe (`apiEndpoint`, pas `host`) : le brouillon de l'editeur range tous les groupes dans un
 * seul objet.
 */
export const API_KEY_FIELDS: readonly TypedFieldSpec[] = [
  { key: 'apiKeyType', label: 'Type de cle', placeholder: 'Bearer, HMAC, OAuth…' },
  { key: 'apiEndpoint', label: 'Adresse de l’API', placeholder: 'https://api.example.eu', wide: true },
  { key: 'apiValidFrom', label: 'Valable a partir du', placeholder: 'AAAA-MM-JJ' },
  { key: 'apiExpiry', label: 'Expire le', placeholder: 'AAAA-MM-JJ' },
  { key: 'apiFileName', label: 'Fichier associe', placeholder: 'service-account.json' },
];

export function isApiKeyDateKey(key: string): boolean {
  return key === 'apiValidFrom' || key === 'apiExpiry';
}

/**
 * Parametres d'adresse qui portent un secret : une cle collee dans l'URL (`?api_key=…`, `?access_token=…`) finirait en
 * clair dans un champ cherchable, hors de l'historique et de la Sante du coffre -- et partirait dans les journaux du
 * serveur a la premiere requete.
 */
const SECRET_QUERY_PARAMETERS = /^(?:api[_-]?key|apikey|key|token|access[_-]?token|auth[_-]?token|secret|client[_-]?secret|password|passwd|pwd|sig|signature)$/iu;

/**
 * Ce qui empeche d'enregistrer la cle : une date illisible, une validite qui commence apres son expiration, ou une
 * adresse d'API qui porte elle-meme un secret (mot de passe avant `@`, parametre `token=`…). Retourne la phrase a
 * afficher, `null` si tout est recevable.
 */
export function apiKeyProblem(fields: Readonly<Record<string, string>>): string | null {
  const validFrom = (fields['apiValidFrom'] ?? '').trim();
  const expiry = (fields['apiExpiry'] ?? '').trim();
  const from = validFrom.length > 0 ? normalizeDocumentDate(validFrom) : '';
  const to = expiry.length > 0 ? normalizeDocumentDate(expiry) : '';
  if (from === null || to === null) return 'Date illisible : AAAA-MM-JJ ou JJ/MM/AAAA attendu (ex. 2027-03-31).';
  // `AAAA-MM-JJ` se compare comme une chaine.
  if (from.length > 0 && to.length > 0 && from > to) return 'La cle expirerait avant d’etre valable : verifiez les deux dates.';
  const endpoint = (fields['apiEndpoint'] ?? '').trim();
  if (endpoint.length > 0) {
    let url: URL | null = null;
    try {
      url = new URL(endpoint.includes('://') ? endpoint : `https://${endpoint}`);
    } catch {
      url = null;
    }
    const secretParameter = url === null ? false : [...url.searchParams.keys()].some((name) => SECRET_QUERY_PARAMETERS.test(name));
    if (url !== null && (url.password.length > 0 || secretParameter)) {
      return 'L’adresse de l’API porte un secret (mot de passe ou parametre de cle) : la cle a sa propre ligne, l’adresse reste nue.';
    }
  }
  return null;
}

/** Service reconnu au prefixe d'une cle, et nature de la cle chez ce service. */
export interface ApiKeyProvider {
  readonly service: string;
  readonly kind: string;
}

/**
 * Prefixes PUBLIES par chaque service pour ses cles (documentation de chacun : GitHub « token formats », GitLab « token
 * prefixes », Stripe « API keys », Slack « token types », AWS « IAM identifiers », npm, PyPI, Hugging Face,
 * DigitalOcean, Anthropic, OpenAI, Google Cloud). Du plus long au plus court : `github_pat_` avant `gh`.
 * Un prefixe n'est qu'une indication d'affichage -- il ne valide ni ne revoque rien.
 */
const API_KEY_PREFIXES: readonly { readonly pattern: RegExp; readonly service: string; readonly kind: string }[] = [
  { pattern: /^github_pat_[A-Za-z0-9_]{20,}$/u, service: 'GitHub', kind: 'Jeton d’acces personnel (a granularite fine)' },
  { pattern: /^ghp_[A-Za-z0-9]{30,}$/u, service: 'GitHub', kind: 'Jeton d’acces personnel (classique)' },
  { pattern: /^gho_[A-Za-z0-9]{30,}$/u, service: 'GitHub', kind: 'Jeton OAuth' },
  { pattern: /^ghu_[A-Za-z0-9]{30,}$/u, service: 'GitHub', kind: 'Jeton d’application (utilisateur)' },
  { pattern: /^ghs_[A-Za-z0-9]{30,}$/u, service: 'GitHub', kind: 'Jeton d’application (installation)' },
  { pattern: /^ghr_[A-Za-z0-9]{30,}$/u, service: 'GitHub', kind: 'Jeton de renouvellement' },
  { pattern: /^glpat-[A-Za-z0-9_.-]{20,}$/u, service: 'GitLab', kind: 'Jeton d’acces personnel' },
  { pattern: /^gldt-[A-Za-z0-9_-]{20,}$/u, service: 'GitLab', kind: 'Jeton de deploiement' },
  { pattern: /^sk_live_[A-Za-z0-9]{20,}$/u, service: 'Stripe', kind: 'Cle secrete (production)' },
  { pattern: /^sk_test_[A-Za-z0-9]{20,}$/u, service: 'Stripe', kind: 'Cle secrete (test)' },
  { pattern: /^rk_live_[A-Za-z0-9]{20,}$/u, service: 'Stripe', kind: 'Cle restreinte (production)' },
  { pattern: /^rk_test_[A-Za-z0-9]{20,}$/u, service: 'Stripe', kind: 'Cle restreinte (test)' },
  { pattern: /^whsec_[A-Za-z0-9+/=]{20,}$/u, service: 'Stripe', kind: 'Secret de signature de webhook' },
  { pattern: /^xoxb-[A-Za-z0-9-]{20,}$/u, service: 'Slack', kind: 'Jeton de bot' },
  { pattern: /^xoxp-[A-Za-z0-9-]{20,}$/u, service: 'Slack', kind: 'Jeton utilisateur' },
  { pattern: /^xapp-[A-Za-z0-9-]{20,}$/u, service: 'Slack', kind: 'Jeton d’application' },
  { pattern: /^sk-ant-[A-Za-z0-9_-]{20,}$/u, service: 'Anthropic', kind: 'Cle d’API' },
  { pattern: /^sk-(?:proj|svcacct|admin)-[A-Za-z0-9_-]{20,}$/u, service: 'OpenAI', kind: 'Cle d’API' },
  { pattern: /^npm_[A-Za-z0-9]{36}$/u, service: 'npm', kind: 'Jeton d’acces' },
  { pattern: /^pypi-[A-Za-z0-9_-]{50,}$/u, service: 'PyPI', kind: 'Jeton d’API' },
  { pattern: /^hf_[A-Za-z0-9]{30,}$/u, service: 'Hugging Face', kind: 'Jeton d’acces' },
  { pattern: /^dop_v1_[a-f0-9]{64}$/u, service: 'DigitalOcean', kind: 'Jeton d’acces personnel' },
  { pattern: /^AIza[A-Za-z0-9_-]{35}$/u, service: 'Google Cloud', kind: 'Cle d’API' },
  { pattern: /^AKIA[A-Z2-7]{16}$/u, service: 'AWS', kind: 'ID de cle d’acces (longue duree)' },
  { pattern: /^ASIA[A-Z2-7]{16}$/u, service: 'AWS', kind: 'ID de cle d’acces (temporaire)' },
];

/** Service d'une cle d'apres son prefixe publie ; `null` si aucun ne correspond (cle maison, format inconnu). */
export function apiKeyProvider(key: string): ApiKeyProvider | null {
  const text = key.trim();
  const found = API_KEY_PREFIXES.find(({ pattern }) => pattern.test(text));
  return found === undefined ? null : { service: found.service, kind: found.kind };
}

/**
 * Licence logicielle (« Software License » de 1Password). La cle de licence est masquee (fiche, editeur) et jamais
 * cherchee, comme le numero d'une carte : elle ne passe PAS par `secrets.password`, la Sante du coffre la jugerait
 * comme un mot de passe (faible, ancienne) alors qu'elle ne se change pas. Zone de texte : une licence hors ligne se
 * colle souvent en bloc de plusieurs lignes. Cles propres au groupe (`licenseCompany`, pas `company` de l'identite) : le
 * brouillon de l'editeur range tous les groupes dans un seul objet.
 */
export const SOFTWARE_LICENSE_FIELDS: readonly TypedFieldSpec[] = [
  { key: 'licenseKey', label: 'Cle de licence', sensitive: true, multiline: true, wide: true, placeholder: 'XXXXX-XXXXX-XXXXX-XXXXX, ou le bloc de licence colle tel quel' },
  { key: 'productVersion', label: 'Version' },
  { key: 'publisherName', label: 'Editeur' },
  { key: 'licensedTo', label: 'Licence au nom de' },
  { key: 'registeredEmail', label: 'E-mail d’enregistrement', inputMode: 'email' },
  { key: 'licenseCompany', label: 'Societe' },
  { key: 'downloadUrl', label: 'Page de telechargement', placeholder: 'https://editeur.example.eu/telecharger', wide: true },
  { key: 'supportEmail', label: 'E-mail du support', inputMode: 'email' },
  { key: 'purchaseDate', label: 'Achetee le', placeholder: 'AAAA-MM-JJ' },
  { key: 'orderNumber', label: 'N° de commande' },
  { key: 'orderTotal', label: 'Montant de la commande' },
  { key: 'softwareExpiry', label: 'Expire le (abonnement, maintenance)', placeholder: 'AAAA-MM-JJ' },
];

export function isSoftwareLicenseDateKey(key: string): boolean {
  return key === 'purchaseDate' || key === 'softwareExpiry';
}

/** Adresse e-mail plausible : une partie locale, un `@`, un domaine a point ; ni espace ni second `@`. */
const EMAIL_ADDRESS = /^[^\s@]+@[^\s@]+\.[^\s@.]+$/u;

/**
 * Ce qui empeche d'enregistrer la licence : une date illisible, un achat posterieur a l'expiration, une adresse e-mail
 * qu'aucun editeur ne pourrait joindre (c'est elle qu'on donne pour recuperer une licence perdue), ou une page de
 * telechargement qui porte un mot de passe avant `@` (il finirait en clair dans un champ cherchable). Retourne la phrase
 * a afficher, `null` si tout est recevable.
 */
export function softwareLicenseProblem(fields: Readonly<Record<string, string>>): string | null {
  const purchase = (fields['purchaseDate'] ?? '').trim();
  const expiry = (fields['softwareExpiry'] ?? '').trim();
  const bought = purchase.length > 0 ? normalizeDocumentDate(purchase) : '';
  const ends = expiry.length > 0 ? normalizeDocumentDate(expiry) : '';
  if (bought === null || ends === null) return 'Date illisible : AAAA-MM-JJ ou JJ/MM/AAAA attendu (ex. 2027-03-31).';
  // `AAAA-MM-JJ` se compare comme une chaine.
  if (bought.length > 0 && ends.length > 0 && bought > ends) return 'La licence expirerait avant d’avoir ete achetee : verifiez les deux dates.';
  for (const [key, label] of [['registeredEmail', 'd’enregistrement'], ['supportEmail', 'du support']] as const) {
    const email = (fields[key] ?? '').trim();
    if (email.length > 0 && !EMAIL_ADDRESS.test(email)) return `Adresse e-mail ${label} invalide (ex. ana@example.eu).`;
  }
  const download = (fields['downloadUrl'] ?? '').trim();
  if (download.length > 0) {
    let url: URL | null = null;
    try {
      url = new URL(download.includes('://') ? download : `https://${download}`);
    } catch {
      url = null;
    }
    if (url !== null && url.password.length > 0) {
      return 'La page de telechargement porte un mot de passe : gardez l’adresse nue, le mot de passe dans un champ masque.';
    }
  }
  return null;
}

/**
 * Serveur (« Server » de 1Password) : l'identifiant et le mot de passe du compte sur le serveur vivent dans
 * `secrets.username` / `secrets.password` -- historique, Sante du coffre (faible, reutilise, fuite) et copie s'y
 * appliquent sans exception. La console d'administration a son propre compte, mot de passe masque (absent de la page et
 * de la recherche, copie lue au clic). Cles propres au groupe (`adminUsername`, pas `username`) : le brouillon de
 * l'editeur range tous les groupes dans un seul objet.
 */
export const SERVER_FIELDS: readonly TypedFieldSpec[] = [
  { key: 'serverUrl', label: 'Adresse du serveur', placeholder: 'https://serveur.example.eu, ssh://serveur.example.eu ou 192.0.2.10', wide: true },
  { key: 'adminConsoleUrl', label: 'Console d’administration', placeholder: 'https://serveur.example.eu:8443', wide: true },
  { key: 'adminUsername', label: 'Identifiant de la console' },
  { key: 'adminPassword', label: 'Mot de passe de la console', sensitive: true },
  { key: 'hostingProvider', label: 'Hebergeur' },
  { key: 'hostingWebsite', label: 'Site de l’hebergeur', placeholder: 'https://hebergeur.example.eu' },
  { key: 'hostingSupportUrl', label: 'Support de l’hebergeur', placeholder: 'https://hebergeur.example.eu/support' },
  { key: 'hostingSupportPhone', label: 'Telephone du support', inputMode: 'tel' },
];

/** Champs d'un serveur qui portent une adresse : jamais un mot de passe, jamais un parametre de cle. */
export const SERVER_ADDRESS_KEYS = ['serverUrl', 'adminConsoleUrl', 'hostingWebsite', 'hostingSupportUrl'] as const;

/** Lit une adresse saisie avec ou sans schema (`serveur.example.eu` -> https) ; `null` si illisible. */
function parseAddress(raw: string): URL | null {
  try {
    return new URL(/^[a-z][a-z0-9+.-]*:\/\//iu.test(raw) ? raw : `https://${raw}`);
  } catch {
    return null;
  }
}

/** Telephone plausible : chiffres, espaces, `+`, points, tirets, parentheses ; au moins 3 chiffres. */
const PHONE_NUMBER = /^\+?[\d\s().-]+$/u;

/** Numero qu'on peut composer (support d'un hebergeur, d'un fournisseur de messagerie). */
function isPhoneNumber(phone: string): boolean {
  return PHONE_NUMBER.test(phone) && phone.replace(/\D/gu, '').length >= 3;
}

/**
 * Ce qui empeche d'enregistrer le serveur : une adresse qui porte un secret (mot de passe avant `@`, parametre
 * `token=` / `password=`…) -- il finirait en clair dans un champ affiche et cherchable, hors de l'historique et de la
 * Sante du coffre --, une adresse avec des espaces (aucun client ne l'ouvrirait), ou un telephone du support qui n'en
 * est pas un. Retourne la phrase a afficher, `null` si tout est recevable.
 */
export function serverProblem(fields: Readonly<Record<string, string>>): string | null {
  const labels: Record<(typeof SERVER_ADDRESS_KEYS)[number], string> = {
    serverUrl: 'du serveur',
    adminConsoleUrl: 'de la console d’administration',
    hostingWebsite: 'du site de l’hebergeur',
    hostingSupportUrl: 'du support de l’hebergeur',
  };
  for (const key of SERVER_ADDRESS_KEYS) {
    const raw = (fields[key] ?? '').trim();
    if (raw.length === 0) continue;
    const url = /\s/u.test(raw) ? null : parseAddress(raw);
    if (url === null) return `Adresse ${labels[key]} illisible : un hote ou une URL sans espace (ex. https://serveur.example.eu).`;
    const secretParameter = [...url.searchParams.keys()].some((name) => SECRET_QUERY_PARAMETERS.test(name));
    if (url.password.length > 0 || secretParameter) {
      return `L’adresse ${labels[key]} porte un secret (mot de passe ou parametre de cle) : gardez l’adresse nue, le mot de passe a sa ligne masquee.`;
    }
  }
  const phone = (fields['hostingSupportPhone'] ?? '').trim();
  if (phone.length > 0 && !isPhoneNumber(phone)) {
    return 'Telephone du support invalide : des chiffres, avec + et espaces si besoin (ex. +33 9 72 10 10 07).';
  }
  return null;
}

/** Hote d'une adresse de serveur (sans schema, compte ni chemin) : de quoi distinguer deux serveurs dans la liste. */
export function serverHost(raw: string): string {
  const url = parseAddress(raw.trim());
  return url === null ? '' : url.hostname;
}

/**
 * Protocole du serveur entrant d'un compte de messagerie : les valeurs du menu « Type » de l'« Email Account » de
 * 1Password, gardees telles quelles pour que l'import n'ait rien a traduire.
 */
export const EMAIL_PROTOCOLS = ['imap', 'pop3'] as const;
export type EmailProtocol = (typeof EMAIL_PROTOCOLS)[number];

/**
 * Securite de la connexion au serveur : TLS des la connexion (`ssl`, RFC 8314 -- ports 993, 995, 465), STARTTLS
 * (passage en TLS apres l'accueil -- ports 143, 110, 587) ou aucune (identifiant et mot de passe en clair sur le reseau).
 */
export const EMAIL_SECURITY_TYPES = ['ssl', 'starttls', 'none'] as const;
export type EmailSecurity = (typeof EMAIL_SECURITY_TYPES)[number];

const EMAIL_PROTOCOL_LABELS: Record<EmailProtocol, string> = { imap: 'IMAP', pop3: 'POP3' };

const EMAIL_SECURITY_LABELS: Record<EmailSecurity, string> = {
  ssl: 'SSL/TLS',
  starttls: 'STARTTLS',
  none: 'Aucune (non chiffree)',
};

/**
 * Compte de messagerie (« Email Account » de 1Password). L'adresse ou l'identifiant et le mot de passe du serveur
 * entrant vivent dans `secrets.username` / `secrets.password` -- historique, Sante du coffre (faible, reutilise, fuite)
 * et copie s'y appliquent sans exception. Le serveur sortant (SMTP) peut avoir son propre compte, mot de passe masque
 * (absent de la page et de la recherche, copie lue au clic). Cles propres au groupe (`incomingPort`, pas `port`) : le
 * brouillon de l'editeur range tous les groupes dans un seul objet.
 */
export const EMAIL_FIELDS: readonly TypedFieldSpec[] = [
  {
    key: 'incomingProtocol',
    label: 'Serveur entrant',
    options: [{ value: '', label: 'Non renseigne' }, ...EMAIL_PROTOCOLS.map((value) => ({ value, label: EMAIL_PROTOCOL_LABELS[value] }))],
  },
  { key: 'incomingServer', label: 'Hote du serveur entrant', placeholder: 'imap.example.eu', wide: true },
  { key: 'incomingPort', label: 'Port entrant', inputMode: 'numeric' },
  {
    key: 'incomingSecurity',
    label: 'Securite (entrant)',
    options: [{ value: '', label: 'Non renseignee' }, ...EMAIL_SECURITY_TYPES.map((value) => ({ value, label: EMAIL_SECURITY_LABELS[value] }))],
  },
  { key: 'smtpServer', label: 'Hote du serveur sortant (SMTP)', placeholder: 'smtp.example.eu', wide: true },
  { key: 'smtpPort', label: 'Port sortant', inputMode: 'numeric' },
  {
    key: 'smtpSecurity',
    label: 'Securite (sortant)',
    options: [{ value: '', label: 'Non renseignee' }, ...EMAIL_SECURITY_TYPES.map((value) => ({ value, label: EMAIL_SECURITY_LABELS[value] }))],
  },
  { key: 'smtpUsername', label: 'Identifiant SMTP (si different)' },
  { key: 'smtpPassword', label: 'Mot de passe SMTP (si different)', sensitive: true },
  { key: 'emailProvider', label: 'Fournisseur' },
  { key: 'emailProviderWebsite', label: 'Site du fournisseur', placeholder: 'https://fournisseur.example.eu' },
  { key: 'emailSupportPhone', label: 'Telephone du support', inputMode: 'tel' },
];

/** Protocole lu tel qu'un export l'ecrit (`IMAP`, `pop`, `POP3`…) ; `null` s'il ne designe aucune valeur connue. */
export function emailProtocol(raw: string): EmailProtocol | null {
  const text = raw.trim().toLowerCase().replace(/\s+/gu, '');
  if (text === 'imap' || text === 'imap4') return 'imap';
  if (text === 'pop' || text === 'pop3') return 'pop3';
  return null;
}

export function emailProtocolLabel(protocol: EmailProtocol): string {
  return EMAIL_PROTOCOL_LABELS[protocol];
}

/**
 * Securite lue telle qu'un export l'ecrit ; `null` si elle ne designe aucune valeur connue. Le « TLS » du menu de
 * 1Password (a cote de « SSL ») et de Thunderbird est STARTTLS ; « SSL/TLS » est le TLS des la connexion.
 */
export function emailSecurity(raw: string): EmailSecurity | null {
  const text = raw.trim().toLowerCase().replace(/[\s_-]+/gu, '');
  if (text === 'ssl' || text === 'ssl/tls' || text === 'implicittls' || text === 'tlsimplicite') return 'ssl';
  if (text === 'starttls' || text === 'tls') return 'starttls';
  if (text === 'none' || text === 'aucune' || text === 'off' || text === 'plain') return 'none';
  return null;
}

export function emailSecurityLabel(security: EmailSecurity): string {
  return EMAIL_SECURITY_LABELS[security];
}

/**
 * Port par defaut d'un serveur (`smtp` pour le sortant), selon sa securite : RFC 8314 (993, 995, 465 en TLS des la
 * connexion), RFC 6409 (587, soumission en STARTTLS), RFC 3501 et 1939 (143, 110 sans TLS des la connexion). `null`
 * si le protocole entrant n'est pas renseigne.
 */
export function emailDefaultPort(server: EmailProtocol | 'smtp' | null, security: EmailSecurity | null): number | null {
  if (server === null) return null;
  const tls = security === 'ssl';
  if (server === 'imap') return tls ? 993 : 143;
  if (server === 'pop3') return tls ? 995 : 110;
  return tls ? 465 : 587;
}

/** Champs d'un compte de messagerie qui portent un hote seul (jamais une adresse, un compte ou un chemin). */
const EMAIL_HOST_KEYS = ['incomingServer', 'smtpServer'] as const;

/**
 * Ce qui empeche d'enregistrer le compte : un hote qui n'en est pas un (adresse `imaps://…`, compte `ana@…`, espace,
 * chemin -- un mot de passe colle la finirait en clair dans un champ cherchable), un port hors de 1-65535, un site du
 * fournisseur qui porte un secret ou un telephone du support illisible. Retourne la phrase a afficher, `null` si tout
 * est recevable.
 */
export function emailAccountProblem(fields: Readonly<Record<string, string>>): string | null {
  const hostLabels: Record<(typeof EMAIL_HOST_KEYS)[number], string> = { incomingServer: 'du serveur entrant', smtpServer: 'du serveur sortant' };
  for (const key of EMAIL_HOST_KEYS) {
    const host = (fields[key] ?? '').trim();
    if (host.length > 0 && /[\s/@?#]/u.test(host)) {
      return `Hote ${hostLabels[key]} illisible : le nom du serveur seul est attendu (ex. imap.example.eu), l’identifiant et le mot de passe ont leurs lignes.`;
    }
  }
  for (const key of ['incomingPort', 'smtpPort'] as const) {
    const port = (fields[key] ?? '').trim();
    if (port.length > 0 && !isPortNumber(port)) {
      return `Port ${key === 'incomingPort' ? 'entrant' : 'sortant'} invalide : un nombre entier de 1 a 65535 est attendu (ex. 993).`;
    }
  }
  const website = (fields['emailProviderWebsite'] ?? '').trim();
  if (website.length > 0) {
    const url = /\s/u.test(website) ? null : parseAddress(website);
    if (url === null) return 'Adresse du site du fournisseur illisible : une URL sans espace (ex. https://fournisseur.example.eu).';
    if (url.password.length > 0 || [...url.searchParams.keys()].some((name) => SECRET_QUERY_PARAMETERS.test(name))) {
      return 'L’adresse du site du fournisseur porte un secret (mot de passe ou parametre de cle) : gardez l’adresse nue.';
    }
  }
  const phone = (fields['emailSupportPhone'] ?? '').trim();
  if (phone.length > 0 && !isPhoneNumber(phone)) {
    return 'Telephone du support invalide : des chiffres, avec + et espaces si besoin (ex. +33 9 72 10 10 07).';
  }
  return null;
}

/**
 * Le compte fait-il circuler son mot de passe en clair ? Un serveur renseigne dont la securite est « aucune » : le
 * mot de passe part tel quel sur le reseau a chaque releve ou envoi.
 */
export function emailAccountInClear(fields: Readonly<Record<string, string>>): boolean {
  return emailSecurity(fields['incomingSecurity'] ?? '') === 'none' || emailSecurity(fields['smtpSecurity'] ?? '') === 'none';
}

/**
 * Nature d'une carte de membre : adhesion (club, association, mutuelle, salle de sport -- « Membership » de 1Password)
 * ou programme de fidelite (compagnie aerienne, hotel, enseigne -- « Reward Program » de 1Password).
 */
export const MEMBERSHIP_KINDS = ['membership', 'reward'] as const;
export type MembershipKind = (typeof MEMBERSHIP_KINDS)[number];

const MEMBERSHIP_KIND_LABELS: Record<MembershipKind, string> = {
  membership: 'Adhesion',
  reward: 'Programme de fidelite',
};

/**
 * Carte de membre ou de fidelite. Le numero de membre se montre a la caisse ou au comptoir : il reste lisible et
 * cherchable, comme sur la carte. Le code PIN (« pin » de 1Password) est masque, absent de la page et de la recherche.
 * Dates au mois pres (1Password les ecrit `MM/AAAA`) ou au jour pres. Cles propres au groupe (`membershipPin`, pas
 * `pin` de la carte bancaire) : le brouillon de l'editeur range tous les groupes dans un seul objet.
 */
export const MEMBERSHIP_FIELDS: readonly TypedFieldSpec[] = [
  {
    key: 'membershipKind',
    label: 'Nature',
    options: [{ value: '', label: 'Non renseignee' }, ...MEMBERSHIP_KINDS.map((value) => ({ value, label: MEMBERSHIP_KIND_LABELS[value] }))],
  },
  { key: 'organization', label: 'Organisme', placeholder: 'Club, association, compagnie, enseigne' },
  { key: 'memberName', label: 'Nom du membre' },
  { key: 'membershipNumber', label: 'N° de membre' },
  { key: 'additionalNumber', label: 'N° complementaire' },
  { key: 'membershipPin', label: 'Code PIN', sensitive: true },
  { key: 'memberSince', label: 'Membre depuis', placeholder: 'MM/AAAA' },
  { key: 'membershipExpiry', label: 'Expire le', placeholder: 'MM/AAAA ou AAAA-MM-JJ' },
  { key: 'membershipWebsite', label: 'Site', placeholder: 'https://club.example.eu/espace-membre', wide: true },
  { key: 'membershipPhone', label: 'Telephone (service client)', inputMode: 'tel' },
  { key: 'reservationsPhone', label: 'Telephone des reservations', inputMode: 'tel' },
];

/** Nature lue telle qu'un export l'ecrit ; `null` si elle ne designe aucune valeur connue. */
export function membershipKind(raw: string): MembershipKind | null {
  const text = raw.trim().toLowerCase();
  return MEMBERSHIP_KINDS.find((value) => value === text) ?? null;
}

export function membershipKindLabel(kind: MembershipKind): string {
  return MEMBERSHIP_KIND_LABELS[kind];
}

export function isMembershipDateKey(key: string): boolean {
  return key === 'memberSince' || key === 'membershipExpiry';
}

/** Date d'une carte de membre : au jour (`AAAA-MM-JJ`) ou au mois (`MM/AAAA`) ; `null` si illisible. */
export function normalizeMembershipDate(value: string): string | null {
  return normalizeDocumentDate(value) ?? normalizeExpiry(value);
}

/**
 * Periode couverte par une date de carte de membre, en ms UTC : le jour indique, ou le mois entier (une carte « 03/2027 »
 * vaut jusqu'au 31 mars inclus, comme une carte bancaire). Fin exclusive ; `null` si la date est illisible.
 */
function membershipPeriod(value: string): { readonly start: number; readonly end: number } | null {
  const normalized = normalizeMembershipDate(value);
  if (normalized === null) return null;
  if (normalized.includes('-')) {
    const [year, month, day] = normalized.split('-').map(Number) as [number, number, number];
    return { start: Date.UTC(year, month - 1, day), end: Date.UTC(year, month - 1, day + 1) };
  }
  const [month, year] = normalized.split('/').map(Number) as [number, number];
  return { start: Date.UTC(year, month - 1, 1), end: Date.UTC(year, month, 1) };
}

/** La carte expire a la fin du jour, ou du mois, indique. `false` si la date est illisible : elle n'affirme rien. */
export function isMembershipExpired(expiry: string, now: number = Date.now()): boolean {
  const period = membershipPeriod(expiry);
  return period !== null && now >= period.end;
}

/**
 * Ce qui empeche d'enregistrer la carte : une date illisible, une adhesion qui commencerait apres son expiration, un
 * site qui porte un secret (mot de passe avant `@`, parametre `token=`…) -- il finirait en clair dans un champ affiche
 * et cherchable --, ou un telephone qu'on ne peut pas composer. Retourne la phrase a afficher, `null` si tout est
 * recevable.
 */
export function membershipProblem(fields: Readonly<Record<string, string>>): string | null {
  const since = (fields['memberSince'] ?? '').trim();
  const expiry = (fields['membershipExpiry'] ?? '').trim();
  const start = since.length > 0 ? membershipPeriod(since) : undefined;
  const end = expiry.length > 0 ? membershipPeriod(expiry) : undefined;
  if (start === null || end === null) return 'Date illisible : MM/AAAA ou AAAA-MM-JJ attendu (ex. 03/2027).';
  // Meme mois ou meme jour : recevable (adhesion d'un jour, d'un mois).
  if (start !== undefined && end !== undefined && start.start >= end.end) {
    return 'La carte expirerait avant d’etre ouverte : verifiez « Membre depuis » et « Expire le ».';
  }
  const website = (fields['membershipWebsite'] ?? '').trim();
  if (website.length > 0) {
    const url = /\s/u.test(website) ? null : parseAddress(website);
    if (url === null) return 'Adresse du site illisible : une URL sans espace (ex. https://club.example.eu).';
    const keyInQuery = [...url.searchParams.keys()].some((name) => SECRET_QUERY_PARAMETERS.test(name));
    if (url.password.length > 0 || keyInQuery) {
      return 'L’adresse du site porte un secret (mot de passe ou parametre de cle) : gardez l’adresse nue, le PIN a sa ligne masquee.';
    }
  }
  for (const [key, label] of [['membershipPhone', 'du service client'], ['reservationsPhone', 'des reservations']] as const) {
    const phone = (fields[key] ?? '').trim();
    if (phone.length > 0 && !isPhoneNumber(phone)) return `Telephone ${label} invalide : des chiffres, avec + et espaces si besoin (ex. +33 1 23 45 67 89).`;
  }
  return null;
}

/**
 * Dossier medical (« Medical Record » de 1Password) : une consultation, son lieu, le praticien, le patient, et le
 * traitement prescrit. Donnees de sante = categorie particuliere du RGPD (art. 9) : le motif, le medicament, la
 * posologie et les remarques sont MASQUES, absents de la page tant qu'ils ne sont pas reveles et jamais cherches --
 * on retrouve un dossier par le praticien, l'etablissement ou le patient, pas par une pathologie lue par-dessus
 * l'epaule. Cles propres au groupe : le brouillon de l'editeur range tous les groupes dans un seul objet.
 */
export const MEDICAL_FIELDS: readonly TypedFieldSpec[] = [
  { key: 'recordDate', label: 'Date', placeholder: 'AAAA-MM-JJ' },
  { key: 'healthcareProfessional', label: 'Professionnel de sante', placeholder: 'Dr Ana Martin, cardiologue' },
  { key: 'medicalFacility', label: 'Lieu', placeholder: 'Cabinet, hopital, clinique' },
  { key: 'medicalPhone', label: 'Telephone', inputMode: 'tel' },
  { key: 'patientName', label: 'Patient' },
  { key: 'visitReason', label: 'Motif de la consultation', sensitive: true, multiline: true, wide: true },
  { key: 'medication', label: 'Medicament', sensitive: true },
  { key: 'dosage', label: 'Posologie', sensitive: true },
  { key: 'medicationNotes', label: 'Remarques sur le traitement', sensitive: true, multiline: true, wide: true },
];

/**
 * Ce qui empeche d'enregistrer le dossier : une date illisible (elle ne se trierait ni ne s'exporterait), ou un
 * telephone qu'on ne peut pas composer. Retourne la phrase a afficher, `null` si tout est recevable. Une date a venir
 * reste recevable : c'est un rendez-vous pris.
 */
export function medicalProblem(fields: Readonly<Record<string, string>>): string | null {
  const date = (fields['recordDate'] ?? '').trim();
  if (date.length > 0 && normalizeDocumentDate(date) === null) return 'Date illisible : AAAA-MM-JJ ou JJ/MM/AAAA attendu (ex. 2026-03-14).';
  const phone = (fields['medicalPhone'] ?? '').trim();
  if (phone.length > 0 && !isPhoneNumber(phone)) return 'Telephone invalide : des chiffres, avec + et espaces si besoin (ex. +33 1 23 45 67 89).';
  return null;
}

/** Pieces d'identite datees d'une entree « identite » : la cle de leur date de fin de validite. */
export type IdentityDocument = 'idCard' | 'passport' | 'license';

export const IDENTITY_DOCUMENTS: readonly { readonly document: IdentityDocument; readonly expiryKey: string; readonly label: string }[] = [
  { document: 'passport', expiryKey: 'passportExpiry', label: 'Passeport' },
  { document: 'idCard', expiryKey: 'idCardExpiry', label: 'Carte d’identite' },
  { document: 'license', expiryKey: 'licenseExpiry', label: 'Permis de conduire' },
];

const DOCUMENT_EXPIRY_KEYS: ReadonlySet<string> = new Set(IDENTITY_DOCUMENTS.map(({ expiryKey }) => expiryKey));

export function isDocumentExpiryKey(key: string): boolean {
  return DOCUMENT_EXPIRY_KEYS.has(key);
}

export const TYPE_LABELS: Record<VaultItemType, string> = {
  login: 'Identifiant',
  card: 'Carte bancaire',
  identity: 'Identite',
  note: 'Note securisee',
  'ssh-key': 'Cle SSH',
  wifi: 'Reseau Wi-Fi',
  bank: 'Compte bancaire',
  wallet: 'Portefeuille crypto',
  database: 'Base de donnees',
  'api-key': 'Cle d’API',
  'software-license': 'Licence logicielle',
  server: 'Serveur',
  email: 'Compte de messagerie',
  membership: 'Carte de membre',
  medical: 'Dossier medical',
};

/** Groupes de champs types, dans l'ordre ou l'editeur et la recherche les parcourent. */
export const TYPED_FIELD_GROUPS: readonly TypedFieldGroup[] = ['card', 'identity', 'wifi', 'bank', 'wallet', 'database', 'api-key', 'software-license', 'server', 'email', 'membership', 'medical'];

export function fieldSpecs(group: TypedFieldGroup): readonly TypedFieldSpec[] {
  if (group === 'database') return DATABASE_FIELDS;
  if (group === 'api-key') return API_KEY_FIELDS;
  if (group === 'software-license') return SOFTWARE_LICENSE_FIELDS;
  if (group === 'server') return SERVER_FIELDS;
  if (group === 'email') return EMAIL_FIELDS;
  if (group === 'membership') return MEMBERSHIP_FIELDS;
  if (group === 'medical') return MEDICAL_FIELDS;
  return group === 'card' ? CARD_FIELDS : group === 'identity' ? IDENTITY_FIELDS : group === 'wifi' ? WIFI_FIELDS : group === 'bank' ? BANK_FIELDS : WALLET_FIELDS;
}

export function typedFieldGroup(type: VaultItemType): TypedFieldGroup | null {
  return type === 'card' || type === 'identity' || type === 'wifi' || type === 'bank' || type === 'wallet' || type === 'database' || type === 'api-key' ||
    type === 'software-license' || type === 'server' || type === 'email' || type === 'membership' || type === 'medical'
    ? type
    : null;
}

/** Lit un groupe de champs depuis des secrets dechiffres : seules les chaines connues sont retenues. */
export function readTypedFields(secrets: EntrySecrets, group: TypedFieldGroup): TypedFields {
  const raw = secrets[group];
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const source = raw as Record<string, unknown>;
  const fields: Record<string, string> = {};
  for (const { key } of fieldSpecs(group)) {
    const value = source[key];
    if (typeof value === 'string' && value.length > 0) fields[key] = value;
  }
  return fields;
}

/**
 * Normalise une saisie : espaces superflus retires, numero de carte en chiffres, expiration en `MM/AAAA`, fin de
 * validite d'une piece d'identite en `AAAA-MM-JJ`, champs vides omis.
 */
export function normalizeTypedFields(group: TypedFieldGroup, input: Readonly<Record<string, string>>): TypedFields {
  const fields: Record<string, string> = {};
  for (const { key } of fieldSpecs(group)) {
    let value = (input[key] ?? '').trim();
    if (group === 'card' && (key === 'number' || key === 'cvv' || key === 'pin')) value = value.replace(/[\s-]+/gu, '');
    if (group === 'card' && key === 'expiry') value = normalizeExpiry(value) ?? value;
    if (group === 'identity' && isDocumentExpiryKey(key)) value = normalizeDocumentDate(value) ?? value;
    // Le SSID garde ses espaces : ils font partie du nom diffuse par la borne.
    if (group === 'wifi' && key === 'ssid') value = input[key] ?? '';
    if (group === 'wifi' && key === 'security') value = wifiSecurity(value) ?? '';
    if (group === 'wifi' && key === 'hidden') value = ['true', '1', 'yes', 'oui'].includes(value.toLowerCase()) ? 'true' : '';
    // IBAN et BIC au format electronique : c'est celui que l'on colle dans un ordre de virement.
    if (group === 'bank' && key === 'iban') value = normalizeIban(value);
    if (group === 'bank' && key === 'bic') value = value.replace(/\s+/gu, '').toUpperCase();
    if (group === 'bank' && key === 'accountType') value = bankAccountType(value) ?? '';
    // Un seul espace entre les mots : la phrase se recopie mot a mot, un saut de ligne colle ne doit rien y ajouter.
    if (group === 'wallet' && key === 'recoveryPhrase') value = normalizeRecoveryPhrase(value);
    // La phrase de passe BIP39 entre telle quelle dans la derivation : un espace de bord retire, c'est un autre portefeuille.
    if (group === 'wallet' && key === 'seedPassphrase') value = input[key] ?? '';
    if (group === 'database' && key === 'engine') value = databaseEngine(value) ?? '';
    if (group === 'api-key' && isApiKeyDateKey(key)) value = normalizeDocumentDate(value) ?? value;
    if (group === 'software-license' && isSoftwareLicenseDateKey(key)) value = normalizeDocumentDate(value) ?? value;
    // Un mot de passe garde ses espaces de bord : la console les exige tels quels.
    if (group === 'server' && key === 'adminPassword') value = input[key] ?? '';
    if (group === 'email' && key === 'smtpPassword') value = input[key] ?? '';
    if (group === 'email' && key === 'incomingProtocol') value = emailProtocol(value) ?? '';
    if (group === 'email' && (key === 'incomingSecurity' || key === 'smtpSecurity')) value = emailSecurity(value) ?? '';
    if (group === 'membership' && key === 'membershipKind') value = membershipKind(value) ?? '';
    if (group === 'membership' && isMembershipDateKey(key)) value = normalizeMembershipDate(value) ?? value;
    if (group === 'medical' && key === 'recordDate') value = normalizeDocumentDate(value) ?? value;
    if (value.length > 0) fields[key] = value;
  }
  return fields;
}

/** Controle de Luhn (ISO/IEC 7812-1) sur 12 a 19 chiffres ; espaces et tirets toleres. */
export function isLuhnValid(number: string): boolean {
  const digits = number.replace(/[\s-]+/gu, '');
  if (!/^\d{12,19}$/u.test(digits)) return false;
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    let digit = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum % 10 === 0;
}

/** Reseau de la carte d'apres ses premiers chiffres (plages IIN publiques) ; `''` si inconnu. */
export function detectCardBrand(number: string): string {
  const d = number.replace(/[\s-]+/gu, '');
  const prefix = (length: number): number => Number(d.slice(0, length));
  if (/^4/u.test(d)) return 'Visa';
  if ((prefix(2) >= 51 && prefix(2) <= 55) || (prefix(4) >= 2221 && prefix(4) <= 2720)) return 'Mastercard';
  if (/^3[47]/u.test(d)) return 'American Express';
  if (/^(6011|65|64[4-9])/u.test(d)) return 'Discover';
  if (/^35(2[89]|[3-8])/u.test(d)) return 'JCB';
  if (/^3(0[0-5]|[689])/u.test(d)) return 'Diners Club';
  if (/^62/u.test(d)) return 'UnionPay';
  if (/^(5018|5020|5038|56|57|58|6304|67)/u.test(d)) return 'Maestro';
  return '';
}

/** `MM/AA`, `MM/AAAA`, `MMAA`, `AAAA-MM` -> `MM/AAAA` ; `null` si illisible ou mois hors 01-12. */
export function normalizeExpiry(value: string): string | null {
  const text = value.trim();
  let month: number;
  let year: number;
  let match = /^(\d{1,2})\s*[/.-]\s*(\d{2}|\d{4})$/u.exec(text) ?? /^(\d{2})(\d{2})$/u.exec(text);
  if (match !== null) {
    month = Number(match[1]);
    year = Number(match[2]);
  } else if ((match = /^(\d{4})-(\d{1,2})$/u.exec(text)) !== null) {
    year = Number(match[1]);
    month = Number(match[2]);
  } else {
    return null;
  }
  if (month < 1 || month > 12) return null;
  if (year < 100) year += 2000;
  return `${String(month).padStart(2, '0')}/${year}`;
}

/** Une carte expire a la fin de son mois d'expiration. `false` si l'expiration est illisible. */
export function isCardExpired(expiry: string, now: number = Date.now()): boolean {
  const normalized = normalizeExpiry(expiry);
  if (normalized === null) return false;
  const [month, year] = normalized.split('/').map(Number) as [number, number];
  return now >= Date.UTC(year, month, 1);
}

/**
 * Date d'une piece d'identite : `AAAA-MM-JJ`, `JJ/MM/AAAA`, `JJ.MM.AAAA`, `JJ-MM-AAAA` -> `AAAA-MM-JJ` ;
 * `null` si illisible ou si le jour n'existe pas (31 avril, 29 fevrier hors annee bissextile).
 */
export function normalizeDocumentDate(value: string): string | null {
  const text = value.trim();
  let day: number;
  let month: number;
  let year: number;
  let match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/u.exec(text);
  if (match !== null) {
    year = Number(match[1]);
    month = Number(match[2]);
    day = Number(match[3]);
  } else if ((match = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/u.exec(text)) !== null) {
    day = Number(match[1]);
    month = Number(match[2]);
    year = Number(match[3]);
  } else {
    return null;
  }
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Une piece d'identite reste valable tout le jour indique (« valable jusqu'au ») et expire le lendemain a 0 h UTC.
 * `false` si la date est illisible : elle n'affirme rien.
 */
export function isDocumentExpired(date: string, now: number = Date.now()): boolean {
  const normalized = normalizeDocumentDate(date);
  if (normalized === null) return false;
  const [year, month, day] = normalized.split('-').map(Number) as [number, number, number];
  return now >= Date.UTC(year, month - 1, day + 1);
}

/** Numero groupe par 4 pour la lecture (American Express : 4-6-5). */
export function formatCardNumber(number: string): string {
  const d = number.replace(/[\s-]+/gu, '');
  if (/^3[47]\d{13}$/u.test(d)) return `${d.slice(0, 4)} ${d.slice(4, 10)} ${d.slice(10)}`;
  return d.replace(/(.{4})(?=.)/gu, '$1 ');
}

/** `•••• 4242` : seuls les 4 derniers chiffres, jamais davantage. */
export function maskCardNumber(number: string): string {
  const d = number.replace(/[\s-]+/gu, '');
  return d.length <= 4 ? '••••' : `•••• ${d.slice(-4)}`;
}

/** Sous-titre de liste, sans jamais exposer de valeur sensible au-dela des 4 derniers chiffres. */
export function typedEntrySubtitle(type: VaultItemType, secrets: EntrySecrets): string {
  if (type === 'card') {
    const card = readTypedFields(secrets, 'card');
    if (card['number'] === undefined) return card['cardholder'] ?? '';
    return [detectCardBrand(card['number']), maskCardNumber(card['number'])].filter(Boolean).join(' ');
  }
  if (type === 'identity') {
    const identity = readTypedFields(secrets, 'identity');
    const fullName = [identity['firstName'], identity['lastName']].filter(Boolean).join(' ');
    return fullName || identity['email'] || '';
  }
  if (type === 'wifi') return readTypedFields(secrets, 'wifi')['ssid'] ?? '';
  if (type === 'bank') {
    // Banque et IBAN masque : de quoi distinguer deux comptes sans rien montrer de plus que quatre caracteres.
    const bank = readTypedFields(secrets, 'bank');
    const account = bank['iban'] !== undefined ? maskIban(bank['iban']) : bank['accountNumber'] !== undefined ? maskCardNumber(bank['accountNumber']) : '';
    return [bank['bankName'], account].filter(Boolean).join(' · ');
  }
  if (type === 'wallet') {
    // Application et reseau, sinon le debut de l'adresse publique : jamais un mot de la phrase.
    const wallet = readTypedFields(secrets, 'wallet');
    const label = [wallet['walletApp'], wallet['blockchain']].filter(Boolean).join(' · ');
    return label || (wallet['walletAddress'] !== undefined ? shortWalletAddress(wallet['walletAddress']) : '');
  }
  if (type === 'database') {
    // Moteur et serveur : de quoi distinguer la production de la recette sans rien montrer de secret.
    const database = readTypedFields(secrets, 'database');
    const engine = databaseEngine(database['engine'] ?? '');
    return [engine === null || engine === 'other' ? '' : databaseEngineLabel(engine), database['host']].filter(Boolean).join(' · ');
  }
  if (type === 'api-key') {
    // Service reconnu au prefixe de la cle et adresse de l'API : jamais un caractere de la cle elle-meme.
    const apiKey = readTypedFields(secrets, 'api-key');
    // L'ID de cle AWS (`AKIA…`) est l'identifiant de l'entree, la cle secrete n'a pas de prefixe.
    const provider = apiKeyProvider(typeof secrets.password === 'string' ? secrets.password : '') ?? apiKeyProvider(typeof secrets.username === 'string' ? secrets.username : '');
    return [provider?.service ?? apiKey['apiKeyType'], apiKey['apiEndpoint']].filter(Boolean).join(' · ');
  }
  if (type === 'software-license') {
    // Editeur et version, sinon le titulaire : jamais un caractere de la cle de licence.
    const license = readTypedFields(secrets, 'software-license');
    return [license['publisherName'], license['productVersion']].filter(Boolean).join(' · ') || (license['licensedTo'] ?? '');
  }
  if (type === 'server') {
    // Hote seul (jamais le compte d'une adresse importee), puis l'hebergeur.
    const server = readTypedFields(secrets, 'server');
    return [serverHost(server['serverUrl'] ?? ''), server['hostingProvider']].filter(Boolean).join(' · ');
  }
  if (type === 'email') {
    // Adresse du compte (son identifiant), puis le fournisseur ; a defaut, l'hote du serveur entrant. Jamais le mot de
    // passe SMTP.
    const email = readTypedFields(secrets, 'email');
    const account = typeof secrets.username === 'string' ? secrets.username : '';
    return [account || email['incomingServer'], email['emailProvider']].filter(Boolean).join(' · ');
  }
  if (type === 'membership') {
    // Organisme, puis le nom du membre : jamais le PIN.
    const membership = readTypedFields(secrets, 'membership');
    return [membership['organization'], membership['memberName']].filter(Boolean).join(' · ');
  }
  if (type === 'medical') {
    // Praticien et date, sinon le patient : jamais le motif ni le traitement.
    const medical = readTypedFields(secrets, 'medical');
    return [medical['healthcareProfessional'] ?? medical['medicalFacility'] ?? medical['patientName'], medical['recordDate']].filter(Boolean).join(' · ');
  }
  if (type === 'ssh-key') {
    // Algorithme et debut d'empreinte : de quoi distinguer deux cles sans rien montrer de secret.
    const info = parseSshPublicKey(readSshKey(secrets).publicKey);
    return info === null ? '' : `${info.label} · ${info.fingerprint.slice(0, 15)}…`;
  }
  return '';
}
